import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#restoreLayout();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
    const details = target.closest('details');
    if (details) {
      details.open = false;
      details.querySelector('summary')?.focus();
    }
  }

  handleGridToggle({ target }) {
    if (!(target instanceof HTMLDetailsElement)) return;
    target.querySelector('summary')?.setAttribute('aria-expanded', String(target.open));
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    for (const menu of this.querySelectorAll(`[data-grid-columns-menu="${viewport}"]`)) {
      const displayValue = menu.querySelector('[data-grid-value]');
      if (displayValue) displayValue.textContent = value.replace('columns-', '');
    }
  }

  #restoreLayout = () => {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const options = [...this.querySelectorAll(`input[data-grid-viewport="${viewport}"]`)];
    if (!options.length) return;
    const storedLayout = window.Shopify.designMode ? null : sessionStorage.getItem(`product-grid-view-${viewport}`);
    const selected = options.find((option) => option.value === storedLayout) ||
      options.find((option) => option.checked) || options[0];
    for (const option of options) option.checked = option === selected;
    this.#setLayout(selected.value);
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = () => this.#restoreLayout();
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
